import { blue, cyan, grey, orange, purple } from "@mui/material/colors";
import { BillItem, DocumentStatus, DocumentType, Bill } from "../pages/hospital/Billing/types";
import { TYPE_BILL_ITEM } from "../constants/types";
import { stringDatePostgresToDate } from ".";
import { createT } from "../components/pdf/utils/simpleTranslate";

/**
 * Redistributes the "Montant non visible" (HIDDEN_VALUE) amount into the visible
 * medical acts so the patient-facing PDF can hide the line while keeping its value
 * in the total (SHER-335).
 *
 * Rules (see src/pages/hospital/Billing/CONTEXT.md):
 * - Absorbers are CHARGE + SERVICE lines. If a bill has none, the amount falls back to
 *   PACKAGE + PHARMACY_ITEM lines so the hidden value is never dropped from the total
 *   (a CHARGE/SERVICE-only set that left package/pharmacy-only bills undercharged would
 *   reintroduce SHER-335). Only when there is no positive line at all is it a no-op.
 * - The amount is split proportionally to each absorber's subtotal, with the
 *   denominator being the sum of those same subtotals (the full hidden amount is
 *   always absorbed, so PDF total == screen total even with a discount present).
 * - Allocation is whole-unit, largest-remainder (Hamilton): floor each share, then
 *   give the leftover units to the largest fractional remainders. Lines therefore sum
 *   exactly to the hidden amount. NOTE: this assumes a zero-decimal currency (FCFA/XOF,
 *   the CHOM case) — reconciliation is exact to the unit; for a decimal currency the
 *   residual could be off by up to one minor unit.
 * - All HIDDEN_VALUE lines are summed; shares are computed from the base `amount`, so
 *   the function is idempotent (re-running on its own output yields the same result).
 *
 * Absorbed amount is written to the transient `amountAlter` field (effective unit
 * price); the HIDDEN_VALUE line itself is left in place for callers to suppress.
 */
export function redistributeHiddenValue(items: BillItem[]): BillItem[] {
    const PRIMARY_ABSORBERS = [TYPE_BILL_ITEM.CHARGE, TYPE_BILL_ITEM.SERVICE];
    const FALLBACK_ABSORBERS = [TYPE_BILL_ITEM.PACKAGE, TYPE_BILL_ITEM.PHARMACY_ITEM];
    const subtotalOf = (item: BillItem) => Number(item.amount) * Number(item.quantity);
    const pickAbsorbers = (types: number[]) =>
        items.filter((item) => types.includes(Number(item.type)) && subtotalOf(item) > 0);

    const hiddenTotal = Math.round(
        items
            .filter((item) => Number(item.type) === TYPE_BILL_ITEM.HIDDEN_VALUE)
            .reduce((sum, item) => sum + subtotalOf(item), 0)
    );
    if (hiddenTotal <= 0) return items;

    // CHARGE/SERVICE first; if none, fall back to PACKAGE/PHARMACY so the value is never lost.
    let absorbers = pickAbsorbers(PRIMARY_ABSORBERS);
    if (absorbers.length === 0) absorbers = pickAbsorbers(FALLBACK_ABSORBERS);
    const denominator = absorbers.reduce((sum, item) => sum + subtotalOf(item), 0);
    if (denominator <= 0) return items; // no positive line to absorb into -> no-op

    // Largest-remainder allocation of whole units.
    const shares = absorbers.map((item) => {
        const exact = (subtotalOf(item) * hiddenTotal) / denominator;
        const floor = Math.floor(exact);
        return { item, add: floor, frac: exact - floor };
    });
    let residual = hiddenTotal - shares.reduce((sum, s) => sum + s.add, 0);
    [...shares]
        .sort((a, b) => b.frac - a.frac)
        .slice(0, residual)
        .forEach((s) => { s.add += 1; });

    const addByItem = new Map(shares.map((s) => [s.item, s.add]));
    return items.map((item) => {
        const add = addByItem.get(item);
        if (!add) return item;
        return { ...item, amountAlter: (subtotalOf(item) + add) / Number(item.quantity) };
    });
}

export function calculateTotalBill(items:BillItem[], withoutTypes:number[] = [], officialPrices = false){
    let amountSeparation = [0,0,0,0,0,0,0,0,0]; // 9 slots: BillItem types 0-8 (incl. AUTO_ANAESTHESIA)
    for(let i = 0; i < items.length; i++){
        const currentBillItem = items[i];
        if(!withoutTypes.includes(currentBillItem.type ? parseInt(currentBillItem.type) : 0)){
            const amount = currentBillItem.amountAlter && !officialPrices ? Number(currentBillItem.amountAlter) : Number(currentBillItem.amount);
            amountSeparation[currentBillItem.type ? currentBillItem.type : 0] += Number(amount) * Number(currentBillItem.quantity);
        }
    }
    // SHER-342: AUTO_ANAESTHESIA (index 8) is a regular charge, mirroring the API total.
    const firstAmount = (amountSeparation[0] + amountSeparation[6] + amountSeparation[3] + amountSeparation[5] + amountSeparation[7] + amountSeparation[8]) - (amountSeparation[1] + amountSeparation[4]);
    let discountAmount = 0;
    if(!withoutTypes.includes(2)){
        discountAmount = (firstAmount*amountSeparation[2])/100;
    }
    const total = firstAmount - discountAmount;
    //Redondeo a dos decimales
    return  Math.round(total * 100) / 100
}

// SHER-342 — FE mirror of the API rule (sherwood-api src/utils/bill.utils.ts):
// an act linked to a PERCENT_CHARGE template spawns a paired "[act] - {template.concept}"
// line at template.percent% of the act, rounded to the whole unit. The template
// (concept + percent) reaches the act item as `percentTemplate`: stamped on the add path
// (resolved from the catalogue) and by the API on load. A derived line carries none.
function actPercentTemplate(item: BillItem): { concept: string; percent: number } | null {
    if (item.type === TYPE_BILL_ITEM.AUTO_ANAESTHESIA) return null;
    const t = item.percentTemplate;
    if (!t || t.concept == null || t.percent == null) return null;
    return { concept: t.concept, percent: Number(t.percent) };
}

/**
 * Rebuild the derived percentage lines (general anaesthesia is the first user): strip any
 * existing AUTO_ANAESTHESIA items, then append one immediately after each act that carries a
 * percentTemplate. A pure function of the acts — exactly like the API's saveUpdateBillCore —
 * so adding/removing/repricing an act stays consistent and a line is never doubled (re-running
 * it is idempotent). Each derived line copies the act's sort keys so it renders right after its act.
 */
export function reconcileAnaesthesiaItems(items: BillItem[]): BillItem[] {
    const result: BillItem[] = [];
    for (const item of items) {
        if (item.type === TYPE_BILL_ITEM.AUTO_ANAESTHESIA) continue; // stripped; regenerated below
        result.push(item);
        const template = actPercentTemplate(item);
        if (template) {
            result.push({
                concept: `${item.concept} - ${template.concept}`,
                amount: Math.round(Number(item.amount) * template.percent / 100),
                quantity: item.quantity ?? 1,
                type: TYPE_BILL_ITEM.AUTO_ANAESTHESIA,
                // SHER-342 (M1): the derived line is server-managed with no independent paid
                // checkbox — its payment status follows the parent act, so mirror paid/used.
                paid: item.paid,
                used: item.used,
                order: item.order,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
            });
        }
    }
    return result;
}

export function documentTypeToColor(type:DocumentType){
    switch(type){
        case DocumentType.BUDGET:
            return blue[500];
        case DocumentType.SUMMARY:
            return orange[500];
        case DocumentType.INVOICE:
            return purple[500];
    }
}

export function documentStatusToColor(type:DocumentStatus){
    switch(type){
        case DocumentStatus.DRAFT:
            return cyan[500];
        case DocumentStatus.CLOSED:
            return "#000";
        // SHER-426: give CANCELLED ("Annulée") a colour so its status chip renders
        // as a filled (grey/inactive) circle instead of a transparent one.
        case DocumentStatus.CANCELLED:
            return grey[500];
    }
}

export function documentTypeToString(type:DocumentType){
    switch(type){
        case DocumentType.BUDGET:
            return "budget";
        case DocumentType.SUMMARY:
            return "summary";
        case DocumentType.INVOICE:
            return "invoice";
    }
}

export function getDateFromStringOrDate(date:string | Date){
    if(typeof date === "string" || date instanceof String){
        return stringDatePostgresToDate(date)
    }
    else{
        return date;
    }
}

/**
 * Generates a localized filename for a bill PDF
 * @param bill - The bill object
 * @param locale - The locale code (e.g., 'en', 'es', 'fr')
 * @returns Formatted filename string
 */
export function generateBillPDFFilename(bill: Bill, locale: string): string {
    const t = createT(locale);
    //const filenamePrefix = t('pages.hospital.billing.pdf.filename.prefix', 'bill');
    const documentTypeName = t(
        `pages.hospital.billing.pdf.filename.${documentTypeToString(bill.type)}`, 
        documentTypeToString(bill.type)
    );
    const dateStr = new Date().toISOString().split('T')[0];
    return `${documentTypeName}_${bill.documentNumber}_${dateStr}`;
}

/**
 * Calculates the total tax from bill items
 * @param items - Array of bill items with optional taxAmount
 * @returns Total tax rounded to 2 decimal places
 */
export function calculateTotalTax(items: BillItem[]): number {
    const total = items.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
    return Math.round(total * 100) / 100;
}

/**
 * Calculates the total with tax (gross - discount + tax)
 * @param items - Array of bill items
 * @param totalGross - Gross total of the bill
 * @param totalDiscount - Total discount amount
 * @returns Net total (with tax) rounded to 2 decimal places
 */
export function calculateTotalWithTax(items: BillItem[], totalGross: number, totalDiscount: number): number {
    const totalTax = calculateTotalTax(items);
    return Math.round((totalGross - totalDiscount + totalTax) * 100) / 100;
}

export interface TaxBreakdownGroup {
    taxRateId: number;
    taxBase: number;
    taxAmount: number;
}

/**
 * Groups tax amounts by taxRateId for breakdown display
 * @param items - Array of bill items with taxRateId and taxAmount
 * @returns Array of tax breakdown groups, each with taxRateId, taxBase, and taxAmount
 */
export function groupTaxBreakdown(items: BillItem[]): TaxBreakdownGroup[] {
    const map = new Map<number, TaxBreakdownGroup>();
    for (const item of items) {
        if (!item.taxRateId || !item.taxAmount) continue;
        const existing = map.get(item.taxRateId);
        const itemBase = Number(item.amount) * Number(item.quantity);
        if (existing) {
            existing.taxBase += itemBase;
            existing.taxAmount += Number(item.taxAmount);
        } else {
            map.set(item.taxRateId, {
                taxRateId: item.taxRateId,
                taxBase: itemBase,
                taxAmount: Number(item.taxAmount),
            });
        }
    }
    return Array.from(map.values()).map(entry => ({
        ...entry,
        taxBase: Math.round(entry.taxBase * 100) / 100,
        taxAmount: Math.round(entry.taxAmount * 100) / 100,
    }));
}
